import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { Form } from 'formik-antd';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import SubmitButton from 'components/formik/SubmitButton';
import FormItem from 'components/formik/FormItem';
import BaseAlert from 'components/wrappers/BaseAlert';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  getMembersWithoutAdminRoleShort,
  grantAdminRole,
} from 'redux/reducers/adminSlice';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { BOMemberShortViewModel } from 'api/adminApiClient';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';

const validationSchema = Yup.object({
  userId: Yup.string().required('Field is required'),
});

interface FormValues {
  userId?: string;
}

type Props = {
  onClose: () => void;
  onSuccess: () => Promise<void>;
};

export default function BOAdminModal(props: Props) {
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const [membersList, setMembersList] = useState<BOMemberShortViewModel[]>([]);
  const { onClose, onSuccess } = props;
  const dispatch = useAppDispatch();
  const formRef: any = useRef();

  const loadMembers = async () => {
    const result = await dispatch(getMembersWithoutAdminRoleShort());

    if (getMembersWithoutAdminRoleShort.fulfilled.match(result)) {
      setMembersList(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
    setIsInitialLoaded(true);
  };

  useEffect(() => {
    loadMembers();
    // eslint-disable-next-line
  }, []);

  const handleModalCancel = () => {
    if (formRef.current?.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  const handleSubmit = async (data: FormValues) => {
    const result = await dispatch(grantAdminRole(data.userId || ''));

    if (grantAdminRole.fulfilled.match(result)) {
      BaseAlert.success('New BO Admin was successfully added.');
      onClose();
      onSuccess();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <BaseModal
      title="Add New Admin to Backoffice"
      width={468}
      open
      onCancel={handleModalCancel}
    >
      <Formik
        innerRef={formRef}
        initialValues={{ userId: undefined }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <p>
              You can share access and rights to manage Backoffice with another
              existing Member. Select them below:
            </p>
            <FormItem name="userId" label="New Admin *">
              <FormikSelectWithLoading
                name="userId"
                placeholder="Select new admin"
                isLoading={!isInitialLoaded}
                showSearch
                optionFilterProp="children"
              >
                {membersList.map((option) => (
                  <FormikSelectWithLoading.Option
                    key={`select-teammate-${option.companyId}`}
                    value={option.companyId || ''}
                  >
                    {option.companyName}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </div>
          <BaseModalFooter>
            <Button onClick={handleModalCancel}>Cancel</Button>
            <SubmitButton>Add</SubmitButton>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
